import React, { useEffect } from 'react';
import clsx from 'clsx';
import millify from 'millify';
import dynamic from 'next/dynamic';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { CollectButtonV3 } from '@components/CollectButton/CollectButtonV3';
import { GiftingButton } from '@components/Gifting/GiftingButton';
import { Button } from '@components/main-components/button/Button';
import { ReleasesAllowlistModal } from '@components/modals/AllowlistModal/ReleaseAllowlistModal';
import type { SelectedMintPanel } from '@components/modals/Buy/Checkout/TiersCheckouModal';
import { TiersCheckoutModal } from '@components/modals/Buy/Checkout/TiersCheckouModal';
import {
  getCollectionTypeForMarketplace,
  MarketPlaceCheckoutModal,
} from '@components/modals/Buy/MarketPlaceCheckout';
import { callIfFunction } from '@components/modals/Buy/helpers';
import { TiersLearnMoreModal } from '@components/modals/Tiers/LearnMore';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  type FragmentType,
  getFragment,
  gql,
  MinimalReleaseUpdatesDocument,
  TiersMintComponentFragmentDoc,
} from '@graphql/reactQuery';
import { useMarketPlace } from '@hooks/useMarketPlace';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { SPACE } from '@utils/constants';
import { formatWeiToEthDisplayFree, getSingularOrPlural } from '@utils/helpers';
import { NetworkIndicator } from '../../NetworkIndicator';
import { hasSaleStarted } from '../Edition/useMintState';
import { PanelWrapper } from '../PanelComponents/Wrapper';
import { SocialProofBar } from '../SocialProof';
import { SongInfo } from '../SongInfo';
import { MINT_COMPONENT_HEADING_SUBTEXT } from '../constants';
import { TiersBadges } from './Badges';
import { MintComponentGAPanelSubInfo, TieredMintComponentPanelSubInfo } from './PanelSubInfo';
import { TiersToggle } from './TiersToggle';
import {
  getMaxSupply,
  hasSaleEnded,
  isScheduleSoldOut,
  useHasAccessToTieredSchedule,
  useHasTieredScheduleEligibility,
  useMintSchedule,
} from './data';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment TiersMintComponent on Release {
    id
    auctionContractType
    marketPlaceUrl
    finalQuantity
    type
    title
    ...MintComponentSongInfo
    ...TiersCheckoutReleaseInfo
    ...MarketplaceCheckout
  }
`);

const SecondaryBadge = dynamic(
  () =>
    import('@components/basics/Badges/UrgencyBadge/SecondaryBadge').then(mod => mod.SecondaryBadge),
  {
    ssr: false,
  },
);

const useTiersMintComponent = ({
  contractAddress,
  chainId,
  selectedMintPanel,
}: {
  contractAddress: string;
  chainId: number;
  selectedMintPanel: SelectedMintPanel;
}) => {
  const {
    data,
    isLoading: isLoadingSchedules,
    refetch,
  } = useMintSchedule({
    editionAddress: contractAddress,
    chainId,
  });

  const isLimitedEditionSelected =
    selectedMintPanel === 'VIP_PUBLIC' || selectedMintPanel === 'VIP_PRESALE';

  const isVipOpenEdition = React.useMemo(() => {
    return data?.schedules.vipTier.saleType === 'open-edition';
  }, [data?.schedules.vipTier.saleType]);

  const hasPresale = !!data?.schedules.vipTier.presale;

  const { data: hasPresaleAccess = false } = useHasTieredScheduleEligibility({
    mintSchedule: data?.schedules.vipTier.presale,
    editionAddress: contractAddress,
  });

  /**
   * When user is not active on the limited edition panel,
   * we want to default show presale toggle info
   */
  const selectedTierForLimitedEdition = isLimitedEditionSelected
    ? selectedMintPanel
    : hasPresaleAccess
    ? 'VIP_PRESALE'
    : 'VIP_PUBLIC';

  const isLimitedEditionSoldOut = (() => {
    if (!data?.schedules.vipTier.public) return false;

    const publicSaleSoldOut = isScheduleSoldOut({
      schedule: data.schedules.vipTier.public,
    });

    const publicSaleEnded = hasSaleEnded({
      schedule: data.schedules.vipTier.public,
    });

    return publicSaleSoldOut || publicSaleEnded;
  })();

  const vipTierPrice = React.useMemo(() => {
    if (isLimitedEditionSoldOut) {
      return data?.schedules.vipTier.public?.price.toString()
        ? formatWeiToEthDisplayFree({
            weiValue: data?.schedules.vipTier.public?.price.toString(),
            maxDecimals: 4,
          })
        : null;
    }
    switch (selectedTierForLimitedEdition) {
      case 'VIP_PRESALE':
        return data?.schedules.vipTier.presale?.price.toString()
          ? formatWeiToEthDisplayFree({
              weiValue: data?.schedules.vipTier.presale?.price.toString(),
              maxDecimals: 4,
            })
          : null;
      default: {
        return data?.schedules.vipTier.public?.price.toString()
          ? formatWeiToEthDisplayFree({
              weiValue: data?.schedules.vipTier.public?.price.toString(),
              maxDecimals: 4,
            })
          : null;
      }
    }
  }, [
    selectedTierForLimitedEdition,
    data?.schedules.vipTier.presale?.price,
    data?.schedules.vipTier.public?.price,
    isLimitedEditionSoldOut,
  ]);

  const vipTierMaxMinted = React.useMemo(() => {
    if (isLimitedEditionSoldOut) {
      return data?.schedules.vipTier.public ? getMaxSupply(data.schedules.vipTier.public) : 0;
    }
    switch (selectedTierForLimitedEdition) {
      case 'VIP_PRESALE':
        return data?.schedules.vipTier.presale ? getMaxSupply(data.schedules.vipTier.presale) : 0;
      default:
        return data?.schedules.vipTier.public ? getMaxSupply(data.schedules.vipTier.public) : 0;
    }
  }, [
    data?.schedules.vipTier.presale,
    data?.schedules.vipTier.public,
    selectedTierForLimitedEdition,
    isLimitedEditionSoldOut,
  ]);

  const vipTierMinted = React.useMemo(() => {
    if (isLimitedEditionSoldOut) {
      return data?.schedules.vipTier.public?.tierInfo.minted;
    }
    switch (selectedTierForLimitedEdition) {
      case 'VIP_PRESALE':
        return data?.schedules.vipTier.presale?.minted;
      default:
        return data?.schedules.vipTier.public?.tierInfo.minted;
    }
  }, [
    isLimitedEditionSoldOut,
    selectedTierForLimitedEdition,
    data?.schedules.vipTier.public?.tierInfo.minted,
    data?.schedules.vipTier.presale?.minted,
  ]);

  const { data: canMintPublicGaSale = false } = useHasAccessToTieredSchedule({
    mintSchedule: data?.schedules.gaTier.public,
    editionAddress: contractAddress,
    chainId,
  });

  const { data: canMintLimitedPublicSale = false } = useHasAccessToTieredSchedule({
    mintSchedule: data?.schedules.vipTier.public,
    editionAddress: contractAddress,
    chainId,
  });

  const { data: canMintLimitedPresale = false } = useHasAccessToTieredSchedule({
    mintSchedule: data?.schedules.vipTier.presale,
    editionAddress: contractAddress,
    chainId,
  });

  const hasVipPublicSaleStarted = (() => {
    // we don't have a start time it means sale hasn't started
    if (!data?.schedules.vipTier.public?.startTime) return false;

    return hasSaleStarted({
      startTimestamp: data.schedules.vipTier.public.startTime,
    });
  })();

  const hasVipPresaleStarted = (() => {
    // we don't have a start time it means sale hasn't started
    if (!data?.schedules.vipTier.presale?.startTime) return false;

    return hasSaleStarted({
      startTimestamp: data.schedules.vipTier.presale.startTime,
    });
  })();

  const vipPresaleMerkleRoot = (() => {
    return data?.schedules.vipTier.presale?.merkleRoot ?? null;
  })();

  const userHitMaxLimit = React.useMemo(() => {
    switch (selectedMintPanel) {
      case 'GA_PUBLIC': {
        // we don't have a start time so we disable buy button
        if (!data?.schedules.gaTier.public?.startTime) return true;

        const saleStarted = hasSaleStarted({
          startTimestamp: data.schedules.gaTier.public.startTime,
        });

        // sale is running
        if (saleStarted) {
          // based on if they can mint more or not we show the button
          return !Boolean(canMintPublicGaSale);
        }

        return true;
      }
      case 'VIP_PUBLIC': {
        // sale is running
        if (hasVipPublicSaleStarted) {
          // based on if they can mint more or not we show the button
          return !Boolean(canMintLimitedPublicSale);
        }
        return true;
      }
      case 'VIP_PRESALE': {
        // sale is running
        if (hasVipPresaleStarted) {
          // user does not have access to presale, so we disable the button
          if (!hasPresaleAccess) return true;

          // based on if they can mint more or not we show the button
          return !Boolean(canMintLimitedPresale);
        }

        return true;
      }
      default:
        // by default we disable the button
        return true;
    }
  }, [
    canMintLimitedPresale,
    canMintLimitedPublicSale,
    canMintPublicGaSale,
    data?.schedules.gaTier.public?.startTime,
    hasPresaleAccess,
    hasVipPresaleStarted,
    hasVipPublicSaleStarted,
    selectedMintPanel,
  ]);

  return {
    data,
    refetch,
    isLoadingSchedules,
    vipTierPrice,
    userHitMaxLimit,
    hasPresale,
    isLimitedEditionSelected,
    vipTierMaxMinted,
    isVipOpenEdition,
    vipTierMinted,
    hasPresaleAccess,
    canMintPublicGaSale,
    hasVipPublicSaleStarted,
    vipPresaleMerkleRoot,
    selectedTierForLimitedEdition,
    isLimitedEditionSoldOut,
  };
};

export function TiersMintComponent({
  contractAddress,
  chainId,
  release,
  isInModal,
  collectButtonSource,
}: {
  contractAddress: string;
  chainId: number;
  isInModal: boolean;
  release: FragmentType<TiersMintComponentFragmentDoc>;
  collectButtonSource: CollectButtonSource;
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  const releaseData = getFragment(TiersMintComponentFragmentDoc, release);
  const [selectedMintPanel, setSelectedMintPanel] = React.useState<SelectedMintPanel>('GA_PUBLIC');
  const { openModal } = ModalContext.useContainer();

  const releaseId = releaseData.id;

  const {
    data,
    isLoadingSchedules,
    vipTierPrice,
    userHitMaxLimit,
    hasPresale,
    isLimitedEditionSelected,
    vipTierMaxMinted,
    isVipOpenEdition,
    hasPresaleAccess,
    vipTierMinted,
    hasVipPublicSaleStarted,
    vipPresaleMerkleRoot,
    selectedTierForLimitedEdition,
    isLimitedEditionSoldOut,
    refetch,
  } = useTiersMintComponent({
    contractAddress,
    chainId,
    selectedMintPanel,
  });

  useEffect(() => {
    return RefetchOnComplete({
      trigger: MinimalReleaseUpdatesDocument,
      refetch,
      filter: ({
        result: {
          data: { releaseUpdates },
        },
      }) => releaseUpdates.id === releaseId,
      throttled: '3 seconds',
    });
  }, [releaseId, refetch]);

  const collectionType = getCollectionTypeForMarketplace({
    auctionContractType: releaseData.auctionContractType,
    releaseType: releaseData.type,
  });
  const { isLoading: isMarketPlaceLoading, marketPlaceFloorPriceInEth } = useMarketPlace({
    releaseId: releaseData.id,
    contractAddress: contractAddress,
    contractChainId: chainId,
    quantity: 0,
    releaseTitle: releaseData.title,
    collectionType,
    marketplaceUrl: releaseData.marketPlaceUrl,
  });

  const selectedLimitedEditionSchedule = React.useMemo(() => {
    switch (selectedTierForLimitedEdition) {
      case 'VIP_PUBLIC':
        return data?.schedules.vipTier.public;
      case 'VIP_PRESALE':
        return data?.schedules.vipTier.presale;
      default:
        return null;
    }
  }, [
    data?.schedules.vipTier.presale,
    data?.schedules.vipTier.public,
    selectedTierForLimitedEdition,
  ]);

  const selectedLimitedEditionsScheduleSoldOut = React.useMemo(() => {
    switch (selectedTierForLimitedEdition) {
      case 'VIP_PRESALE':
        if (!data?.schedules.vipTier.presale) return false;
        return isScheduleSoldOut({
          schedule: data?.schedules.vipTier.presale,
        });
      case 'VIP_PUBLIC':
        if (!data?.schedules.vipTier.public) return false;
        return isScheduleSoldOut({
          schedule: data?.schedules.vipTier.public,
        });
      default:
        return false;
    }
  }, [
    data?.schedules.vipTier.presale,
    data?.schedules.vipTier.public,
    selectedTierForLimitedEdition,
  ]);

  const selectedLimitedEditionsScheduleEnded = React.useMemo(() => {
    switch (selectedTierForLimitedEdition) {
      case 'VIP_PRESALE':
        if (!data?.schedules.vipTier.presale) return false;
        return hasSaleEnded({
          schedule: data?.schedules.vipTier.presale,
        });
      case 'VIP_PUBLIC':
        if (!data?.schedules.vipTier.public) return false;
        return hasSaleEnded({
          schedule: data?.schedules.vipTier.public,
        });
      default:
        return false;
    }
  }, [
    data?.schedules.vipTier.presale,
    data?.schedules.vipTier.public,
    selectedTierForLimitedEdition,
  ]);

  /**
   * - user has hit max limit and public sale has started
   * - sale is sold out
   * - sale has ended
   */
  const isSaleOverOrUserHitLimit =
    selectedLimitedEditionsScheduleSoldOut ||
    selectedLimitedEditionsScheduleEnded ||
    (userHitMaxLimit && hasVipPublicSaleStarted);

  /**
   * Sale is over and we we have listing on secondary we can let users mint secondary on sound
   */
  const showPurchaseSecondaryListings =
    Boolean(marketPlaceFloorPriceInEth) && isSaleOverOrUserHitLimit;

  const onBuy = useStableCallback(() => {
    // there is no secondary for GA public sale
    if (showPurchaseSecondaryListings && selectedMintPanel !== 'GA_PUBLIC') {
      return openModal(ModalType.RELEASE_CHECKOUT, {
        body: (
          <MarketPlaceCheckoutModal
            key={`${releaseId}-tiers-checkout-secondary`}
            releaseData={releaseData}
            contractAddress={contractAddress}
            marketPlacePlaceUrl={releaseData.marketPlaceUrl}
            openGiftingDropdown={false}
          />
        ),
      });
    }

    return openModal(ModalType.RELEASE_CHECKOUT, {
      body: (
        <TiersCheckoutModal
          releaseData={releaseData}
          selectedMintPanel={selectedMintPanel}
          key={`${releaseId}-${selectedMintPanel}-tiers-checkout`}
          openGiftingDropdown={false}
        />
      ),
    });
  });

  const onGift = useStableCallback(() => {
    // there is no secondary for GA public sale
    if (showPurchaseSecondaryListings && selectedMintPanel !== 'GA_PUBLIC') {
      return openModal(ModalType.RELEASE_CHECKOUT, {
        body: (
          <MarketPlaceCheckoutModal
            key={`${releaseId}-tiers-checkout-secondary-gifting`}
            releaseData={releaseData}
            contractAddress={contractAddress}
            marketPlacePlaceUrl={releaseData.marketPlaceUrl}
            openGiftingDropdown
          />
        ),
      });
    }

    return openModal(ModalType.RELEASE_CHECKOUT, {
      body: (
        <TiersCheckoutModal
          releaseData={releaseData}
          selectedMintPanel={selectedMintPanel}
          key={`${releaseId}-${selectedMintPanel}-tiers-checkout-gifting`}
          openGiftingDropdown
        />
      ),
    });
  });

  const openAllowListModal = useStableCallback(() => {
    return openModal(ModalType.RELEASE_ALLOWLIST, {
      body: <ReleasesAllowlistModal releaseId={releaseData.id} merkleRoot={vipPresaleMerkleRoot} />,
    });
  });

  // schedules have loaded and we do not have a GA public sale
  // we want to default to VIP public sale
  React.useEffect(() => {
    if (!isLoadingSchedules && !data?.schedules.gaTier.public) {
      setSelectedMintPanel('VIP_PUBLIC');
    }
  }, [data?.schedules.gaTier.public, isLoadingSchedules]);

  const allowGifting = React.useMemo(
    () =>
      (selectedMintPanel !== 'VIP_PRESALE' ||
        (selectedMintPanel !== 'VIP_PRESALE' && showPurchaseSecondaryListings)) &&
      isAuthenticatedWithAPI,
    [isAuthenticatedWithAPI, selectedMintPanel, showPurchaseSecondaryListings],
  );

  const allowLimitedGifting =
    allowGifting &&
    !(
      selectedLimitedEditionsScheduleEnded ||
      selectedLimitedEditionsScheduleSoldOut ||
      (userHitMaxLimit && hasVipPublicSaleStarted)
    );

  if (isLoadingSchedules || !data?.schedules || isMarketPlaceLoading) {
    return <div className="h-[106px] w-full animate-pulse rounded-lg bg-neutral200" />;
  }

  const vipPrice = showPurchaseSecondaryListings ? marketPlaceFloorPriceInEth : vipTierPrice;

  const maxSupply =
    releaseData.finalQuantity || callIfFunction(data.info?.tierInfo?.[0]?.maxMintable);
  const totalMinted = bigIntToNumber(data.info.totalMinted, { strict: false });
  const gaTier = data.schedules.gaTier.public;

  return (
    <div className="flex w-full flex-col rounded-md lt:max-w-sm">
      <div
        className={clsx(
          'w-full overflow-hidden rounded-t-lg bg-white',
          isInModal ? 'flex border-b border-base200 pb-4' : 'hidden p-4 shadow-tertiary lt:flex',
        )}
      >
        <SongInfo release={releaseData} />
      </div>
      <div
        className={clsx(
          'z-above1 flex w-full flex-col gap-4 bg-white',
          isInModal ? 'pt-4' : 'rounded-t-lg p-4 shadow-tertiary lt:rounded-t-none',
        )}
      >
        {!!gaTier && (
          <PanelWrapper
            selected={selectedMintPanel === 'GA_PUBLIC'}
            subInfo={<MintComponentGAPanelSubInfo startTime={gaTier.startTime} />}
            onClick={() => setSelectedMintPanel('GA_PUBLIC')}
          >
            <div className="flex flex-col gap-1 p-3">
              <div className="flex flex-col gap-2">
                <h2 className="font-title text-title-m font-medium text-base900">
                  {MINT_COMPONENT_HEADING_SUBTEXT.FOREVER.heading}
                </h2>
              </div>

              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-1">
                  <NetworkIndicator chainId={chainId} />
                  <span
                    // YES this is hardcoded.
                    // For GA - we give artist part of platform fee.
                    // So the `price` we read from SDK is the amount artist gets.
                    // Context: https://soundxyz.slack.com/archives/C05BEM19ALF/p1695928542968619?thread_ts=1695928340.914139&cid=C05BEM19ALF
                    className="font-base text-base-m font-semibold"
                  >
                    FREE
                  </span>
                </div>
              </div>

              <div className="text-base-m font-medium text-base500">
                {millify(gaTier.minted ?? 0)}&nbsp;
                {getSingularOrPlural({
                  count: gaTier.minted ?? 0,
                  word: 'mint',
                })}
              </div>

              {selectedMintPanel === 'GA_PUBLIC' && (
                <div className="mt-2 flex w-full gap-2 [&_a]:w-full">
                  <CollectButtonV3
                    releaseId={releaseData.id}
                    marketPlaceUrl={releaseData.marketPlaceUrl}
                    sourceComponent={collectButtonSource}
                    onBuy={onBuy}
                    fullWidth
                    showDisabledCollect={userHitMaxLimit}
                    // Sale is never over
                    isSaleOverOrUserHitLimit={false}
                    // This is always available to mint, so no need to show secondary listings
                    showPurchaseSecondaryListings={false}
                  />
                  {allowGifting && (
                    <GiftingButton
                      releaseId={releaseData.id}
                      sourceComponent="Mint Component"
                      size="L"
                      onClick={onGift}
                      disabled={userHitMaxLimit}
                    />
                  )}
                </div>
              )}
            </div>
          </PanelWrapper>
        )}

        {isLimitedEditionSoldOut ? (
          <PanelWrapper
            selected={isLimitedEditionSelected}
            subInfo={null}
            onClick={() => {
              setSelectedMintPanel('VIP_PUBLIC');
            }}
          >
            <div className="flex flex-col gap-1 p-3">
              <div className="flex flex-row items-center justify-between gap-2">
                <h2 className="limited-animated-text flex items-end font-title text-title-m font-medium">
                  {MINT_COMPONENT_HEADING_SUBTEXT.FALLBACK.heading}
                </h2>
                <SecondaryBadge
                  marketPlaceUrl={releaseData.marketPlaceUrl}
                  className="bg-transparent text-neutral400 hover:text-neutral800"
                />
              </div>

              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-1">
                  <NetworkIndicator chainId={chainId} />
                  <span className="font-base text-base-m font-semibold">{vipPrice}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-base-m font-medium text-base500">
                    {millify(vipTierMinted ?? totalMinted)}
                    {SPACE}
                    {getSingularOrPlural({
                      count: vipTierMinted ?? totalMinted,
                      word: 'mint',
                    })}
                  </p>
                </div>
                {data?.schedules.vipTier.public && (
                  <TiersBadges schedule={data.schedules.vipTier.public} />
                )}
              </div>

              {isLimitedEditionSelected && (
                <div className="mt-2 flex w-full gap-2 [&_a]:w-full">
                  <CollectButtonV3
                    releaseId={releaseData.id}
                    marketPlaceUrl={releaseData.marketPlaceUrl}
                    sourceComponent={collectButtonSource}
                    onBuy={onBuy}
                    showDisabledCollect={
                      selectedLimitedEditionsScheduleEnded ||
                      selectedLimitedEditionsScheduleSoldOut ||
                      userHitMaxLimit
                    }
                    isSaleOverOrUserHitLimit={isAuthenticatedWithAPI && isSaleOverOrUserHitLimit}
                    fullWidth
                    showPurchaseSecondaryListings={showPurchaseSecondaryListings}
                  />
                  {allowLimitedGifting && (
                    <GiftingButton
                      releaseId={releaseData.id}
                      sourceComponent="Mint Component"
                      size="L"
                      onClick={onGift}
                      disabled={userHitMaxLimit || !hasVipPublicSaleStarted}
                    />
                  )}
                </div>
              )}
            </div>
          </PanelWrapper>
        ) : (
          <PanelWrapper
            selected={isLimitedEditionSelected}
            subInfo={
              selectedLimitedEditionSchedule && (
                <TieredMintComponentPanelSubInfo
                  schedule={selectedLimitedEditionSchedule}
                  contractAddress={contractAddress}
                  chainId={chainId}
                  hasPresale={hasPresale}
                  hasPresaleAccess={hasPresaleAccess}
                />
              )
            }
            onClick={() => {
              // we let the toggle handle the click
              if (isLimitedEditionSelected) return;

              // if user has presale access we want to default them to see presale
              if (hasPresaleAccess) {
                return setSelectedMintPanel('VIP_PRESALE');
              }

              setSelectedMintPanel('VIP_PUBLIC');
            }}
          >
            <div className="flex flex-col gap-1 p-3">
              <div className="flex flex-row items-center justify-between gap-2">
                <h2 className="limited-animated-text font-title text-title-m font-medium">
                  {MINT_COMPONENT_HEADING_SUBTEXT.FALLBACK.heading}
                </h2>
                {hasPresale && (
                  <TiersToggle
                    valueState={selectedTierForLimitedEdition}
                    onValueChange={setSelectedMintPanel}
                    presaleEligible={hasPresaleAccess}
                  />
                )}
              </div>

              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-1">
                  <NetworkIndicator chainId={chainId} />
                  <span className="font-base text-base-m font-semibold">{vipPrice}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-base-m font-medium text-base500">
                    {isVipOpenEdition ||
                    selectedLimitedEditionsScheduleSoldOut ||
                    selectedLimitedEditionsScheduleEnded ? (
                      <>
                        {millify(vipTierMinted ?? totalMinted)}
                        {SPACE}
                        {getSingularOrPlural({
                          count: vipTierMinted ?? totalMinted,
                          word: 'mint',
                        })}
                      </>
                    ) : (
                      <>
                        {vipTierMinted ?? totalMinted}/
                        {callIfFunction(vipTierMaxMinted ?? maxSupply)}
                        {SPACE}
                        {getSingularOrPlural({
                          count: vipTierMinted ?? totalMinted,
                          word: 'mint',
                        })}
                      </>
                    )}
                  </p>
                  {isAuthenticatedWithAPI &&
                    hasPresale &&
                    selectedTierForLimitedEdition === 'VIP_PRESALE' && (
                      <div className="flex items-center justify-center text-base-m font-medium text-base500">
                        {SPACE}•{SPACE}
                        {hasPresaleAccess ? (
                          <Button
                            variant="text"
                            size="XS"
                            label="Eligible"
                            className="m-0 h-auto min-w-0 p-0 text-fuchsia500"
                            onClick={openAllowListModal}
                          />
                        ) : (
                          <Button
                            variant="text"
                            size="XS"
                            label="Not Eligible"
                            className="m-0 h-auto min-w-0 p-0 text-base500"
                            onClick={openAllowListModal}
                          />
                        )}
                      </div>
                    )}
                </div>
                {selectedLimitedEditionSchedule && (
                  <TiersBadges schedule={selectedLimitedEditionSchedule} />
                )}
              </div>

              {isLimitedEditionSelected && (
                <div className="mt-2 flex w-full gap-2 [&_a]:w-full">
                  <CollectButtonV3
                    releaseId={releaseData.id}
                    marketPlaceUrl={releaseData.marketPlaceUrl}
                    sourceComponent={collectButtonSource}
                    onBuy={onBuy}
                    showDisabledCollect={
                      selectedLimitedEditionsScheduleEnded ||
                      selectedLimitedEditionsScheduleSoldOut ||
                      userHitMaxLimit
                    }
                    isSaleOverOrUserHitLimit={isAuthenticatedWithAPI && isSaleOverOrUserHitLimit}
                    fullWidth
                    showPurchaseSecondaryListings={showPurchaseSecondaryListings}
                  />
                  {allowLimitedGifting && (
                    <GiftingButton
                      releaseId={releaseData.id}
                      sourceComponent="Mint Component"
                      size="L"
                      onClick={onGift}
                      disabled={userHitMaxLimit || !hasVipPublicSaleStarted}
                    />
                  )}
                </div>
              )}
            </div>
          </PanelWrapper>
        )}
        {!!gaTier && (
          <div className="flex justify-center">
            <Button
              variant="text"
              size="XS"
              onClick={() => {
                openModal(ModalType.TIER_LEARN_MORE, {
                  body: <TiersLearnMoreModal />,
                });
              }}
              className="-my-2 w-full p-0 text-center font-base text-base-s text-base500 outline-base300 hover:text-base600"
              label="Compare Editions"
            />
          </div>
        )}
      </div>
      {!isInModal && <SocialProofBar releaseId={releaseData.id} totalMinted={totalMinted} />}
    </div>
  );
}
